import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import { logout } from "../../app/auth/authSlice.js";

const ManageAddons = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAddons = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ADDONS);

      if (response.data.status) {
        const addonData = response.data.data;
        if (Array.isArray(addonData)) {
          setAddons(addonData);
        } else {
          setAddons([]);
        }
      } else {
        showErrorToast(response.data.message || "Failed to fetch addons");
        setAddons([]);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch addons");
      }
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  const handleAddAddon = () => {
    navigate("/addons/addAddon");
  };

  const handleEditAddon = (addon) => {
    navigate(`/addons/updateAddon/${addon.addonID}`);
  };

  const handleDeleteAddon = async (addonId) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "This will deactivate the addon. It will no longer be available for new subscriptions.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_ADDON, {
          params: { addonID: addonId },
        });

        if (response.data.status) {
          showSuccessToast("Addon deleted successfully");
          fetchAddons();
        } else {
          showErrorToast(response.data.message || "Failed to delete addon");
        }
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to delete addon");
      }
    }
  };

  const handleToggleStatus = async (addonId, currentStatus) => {
    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_ADDON_STATUS,
        null,
        {
          params: {
            addonID: addonId,
            status: !currentStatus,
          },
        }
      );

      if (response.data.status) {
        showSuccessToast("Addon status updated successfully");
        fetchAddons();
      } else {
        showErrorToast(response.data.message || "Failed to update status");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to update addon status");
    }
  };

  const getAddonTypeInfo = (type) => {
    const types = {
      User: { label: "User", icon: "fa-user", color: "#17a2b8" },
      Storage: { label: "Storage", icon: "fa-database", color: "#ffc107" },
      Feature: { label: "Feature", icon: "fa-star", color: "#28a745" },
      Support: { label: "Support", icon: "fa-headset", color: "#dc3545" },
      Location: { label: "Location", icon: "fa-map-marker", color: "#6f42c1" },
      Custom: { label: "Custom", icon: "fa-cog", color: "#6c757d" },
    };
    return types[type] || { label: type, icon: "fa-circle", color: "#6c757d" };
  };

  return (
    <div className="content">
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Manage Addons</li>
                </ol>
                <h1 className="page-header mb-0">Subscription Addons</h1>
              </div>
              <div className="ms-auto">
                <button
                  className="btn px-4"
                  onClick={handleAddAddon}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "25px",
                    fontWeight: "500",
                  }}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
                  Add Addon
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Addon Name</th>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Value</th>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Unit</th>
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Status</th>
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            <Preloader />
                          </td>
                        </tr>
                      ) : addons.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No addons found
                          </td>
                        </tr>
                      ) : (
                        addons.map((addon) => {
                          const typeInfo = getAddonTypeInfo(addon.addonType);
                          return (
                            <tr key={addon.addonID}>
                              <td>
                                <strong>{addon.addonName}</strong>
                                {addon.addonDescription && (
                                  <div className="small" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                                    {addon.addonDescription}
                                  </div>
                                )}
                              </td>
                              <td>{addon.addonValue}</td>
                              <td>{addon.addonUnit || "-"}</td>
                              <td className="text-center">
                                <div className="form-check form-switch d-inline-block">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={addon.isActive}
                                    onChange={() =>
                                      handleToggleStatus(addon.addonID, addon.isActive)
                                    }
                                    style={{ cursor: "pointer" }}
                                  />
                                </div>
                              </td>
                              <td className="text-center">
                                <div className="d-flex justify-content-center gap-2">
                                  {/* Edit Button */}
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '6px',
                                      border: `1px solid ${themeColor}`,
                                      color: themeColor,
                                      transition: 'all 0.2s ease',
                                      padding: 0
                                    }}
                                    onClick={() => handleEditAddon(addon)}
                                    title="Edit Addon"
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = themeColor;
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = themeColor;
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                  >
                                    <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                  </button>

                                  {/* Delete Button */}
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '6px',
                                      border: '1px solid #dc3545',
                                      color: '#dc3545',
                                      transition: 'all 0.2s ease',
                                      padding: 0
                                    }}
                                    onClick={() => handleDeleteAddon(addon.addonID)}
                                    title="Delete Addon"
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#dc3545';
                                      e.currentTarget.style.color = 'white';
                                      e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = '#dc3545';
                                      e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                  >
                                    <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                      Showing {addons.length} addon(s)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageAddons;

